import { Loader2, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useDeleteLoginImageMutation } from '~entities/studios/api/delete-login-image';
import { useUploadLoginImageMutation } from '~entities/studios/api/upload-login-image';

import { ImageCropperDialog } from '~shared/components/image-cropper-dialog';
import { useImageCropper } from '~shared/hooks/useImageCropper';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

import { ImageUploader } from './logo-uploader';

export function LoginImageForm({
  currentUrl,
  isEnabled,
}: {
  currentUrl?: string | null;
  isEnabled?: boolean;
}) {
  const uploadMutation = useUploadLoginImageMutation();
  const deleteMutation = useDeleteLoginImageMutation();

  const [imagePreview, setImagePreview] = useState<string | null>(
    currentUrl ?? null,
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  const handleCropCompleteCallback = useCallback(
    (croppedFile: File, croppedImageUrl: string) => {
      setImageFile(croppedFile);
      setImagePreview(croppedImageUrl);
    },
    [],
  );

  const {
    cropperOpen,
    cropperImageSrc,
    handleImageChange: handleCropperImageChange,
    handleCropComplete,
    setCropperOpen,
  } = useImageCropper({
    onCropComplete: handleCropCompleteCallback,
  });

  async function handleSubmit() {
    if (!imageFile) {
      toast.error('No image selected. Please select a login image to upload.');
      return;
    }

    setIsSubmitting(true);

    try {
      await uploadMutation.mutateAsync(imageFile);
      toast.success('Login page image has been updated successfully.');
      // Reset the file selection after successful upload
      setImageFile(null);
    } catch (error) {
      console.error(error);
      toast.error('There was a problem updating your login image.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDeleteConfirm() {
    setIsSubmitting(true);

    try {
      await deleteMutation.mutateAsync();
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setImagePreview(null);
      setImageFile(null);
      setShowDeleteDialog(false);
      toast.success('Login image deleted successfully');
    } catch (error) {
      console.error(error);
      toast.error('There was a problem deleting your login image.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleImageChange = (file: File | null) => {
    if (!file) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setImageFile(null);
      setImagePreview(null);
      return;
    }
    handleCropperImageChange(file);
  };

  useEffect(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    if (imagePreview && imagePreview.startsWith('blob:')) {
      blobUrlRef.current = imagePreview;
    }

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [imagePreview]);

  return (
    <Card className='mx-auto w-full max-w-2xl'>
      <CardHeader className='space-y-4 pb-6'>
        <div className='flex items-center justify-between'>
          <div className='space-y-2'>
            <CardTitle className='bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-2xl font-semibold text-transparent'>
              Login Page Image
            </CardTitle>
            <p className='text-sm leading-relaxed text-muted-foreground'>
              Upload a custom image for your studio&apos;s login page. Images
              will be automatically optimized to WebP format. If no image is
              uploaded, the default animated gallery will be shown.
            </p>
          </div>
          <div className='inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-2.5 py-0.5 text-xs font-semibold text-purple-700'>
            <Upload className='mr-1 size-3' />
            Branding
          </div>
        </div>
      </CardHeader>
      <CardContent className='pt-4 sm:pt-6'>
        <div className='space-y-2'>
          <ImageUploader
            currentImage={imagePreview}
            onImageChange={handleImageChange}
            maxWidth='100%'
            helpText='Upload a hero image for your login page (JPEG, PNG - max 5MB). Use flexible aspect ratio, will be displayed with object-cover.'
            showChangeButtonOverlay={true}
          />
          <p className='text-xs text-muted-foreground sm:text-sm'>
            This image will be shown on the left side of your parent portal
            login page at /login?studioId=YOUR_ID
          </p>
          {isEnabled && currentUrl && (
            <div className='pt-2'>
              <p className='mb-2 text-xs font-medium text-green-600'>
                ✓ Custom login image is currently active
              </p>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className='flex justify-between pb-4 sm:pb-6'>
        {isEnabled && currentUrl && (
          <Button
            variant='outline'
            onClick={() => {
              setShowDeleteDialog(true);
            }}
            disabled={isSubmitting || deleteMutation.isPending}
            className='text-red-600 hover:text-red-700'
          >
            <X className='mr-2 size-4' />
            Remove Custom Image
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || !imageFile}
          className='ml-auto'
        >
          {isSubmitting ? 'Uploading...' : 'Upload Image'}
        </Button>
      </CardFooter>
      {showDeleteDialog && (
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Custom Login Image?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to remove the custom login page image? The
                default animated gallery will be shown instead.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={deleteMutation.isPending}
                className='bg-red-600 text-white hover:bg-red-700'
              >
                {deleteMutation.isPending && (
                  <Loader2 className='size-4 animate-spin' />
                )}
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {cropperImageSrc && cropperOpen && (
        <ImageCropperDialog
          open={cropperOpen}
          onOpenChange={setCropperOpen}
          imageSrc={cropperImageSrc}
          onCropComplete={handleCropComplete}
          title='Crop Login Page Image'
          description='Adjust your login page image to the desired crop area'
        />
      )}
    </Card>
  );
}
